import DOMPurify from "dompurify";

const SRC_DOC_CSP = [
  "default-src 'none'",
  "base-uri 'none'",
  "object-src 'none'",
  "script-src 'unsafe-inline'",
  "connect-src 'none'",
  "form-action 'none'",
  "img-src https: http: data: blob:",
  "media-src https: http: data: blob:",
  "font-src https: data:",
  "style-src 'unsafe-inline'",
  "frame-src https://www.youtube.com https://youtube.com https://www.youtube-nocookie.com https://player.vimeo.com https://docs.google.com https://view.officeapps.live.com",
].join("; ");

export const PAGE_HTML_SANDBOX = "allow-scripts allow-popups allow-popups-to-escape-sandbox";

const TRUSTED_EMBED_HOSTS = new Set([
  "www.youtube.com", "youtube.com", "www.youtube-nocookie.com",
  "player.vimeo.com", "docs.google.com", "view.officeapps.live.com",
]);

const SAFE_LINK_SCHEMES = new Set(["http:", "https:", "mailto:", "tel:"]);
const SAFE_RESOURCE_SCHEMES = new Set(["http:", "https:", "blob:"]);

const normalizedScheme = (value) => {
  // eslint-disable-next-line no-control-regex -- controls are intentionally removed to defeat `java\nscript:` obfuscation.
  const compact = String(value || "").trim().replace(/[\u0000-\u0020\u007f]+/g, "");
  const match = compact.match(/^([a-z][a-z0-9+.-]*):/i);
  return match ? `${match[1].toLowerCase()}:` : "";
};

export function safePageHref(value, fallback = "#") {
  const raw = value == null ? "" : String(value).trim();
  if (!raw) return fallback;
  if (raw.startsWith("#") || raw.startsWith("/") || raw.startsWith("./") || raw.startsWith("../") || raw.startsWith("?")) return raw;
  if (raw.startsWith("//")) return `https:${raw}`;
  const scheme = normalizedScheme(raw);
  if (!scheme) return raw;
  return SAFE_LINK_SCHEMES.has(scheme) ? raw : fallback;
}

export function safePageResourceUrl(value, { allowDataImage = false } = {}) {
  const raw = value == null ? "" : String(value).trim();
  if (!raw) return "";
  if (raw.startsWith("/") || raw.startsWith("./") || raw.startsWith("../")) return raw;
  if (raw.startsWith("//")) return `https:${raw}`;
  if (allowDataImage && /^data:image\/(?:png|gif|jpe?g|webp|svg\+xml);/i.test(raw)) return raw;
  return SAFE_RESOURCE_SCHEMES.has(normalizedScheme(raw)) ? raw : "";
}

export function safePageEmbedUrl(value) {
  const safe = safePageResourceUrl(value);
  if (!safe || typeof URL === "undefined") return "";
  try {
    const parsed = new URL(safe);
    return parsed.protocol === "https:" && TRUSTED_EMBED_HOSTS.has(parsed.hostname.toLowerCase()) ? parsed.href : "";
  } catch (_error) {
    return "";
  }
}

export function securePageHtmlDocument(html) {
  const clean = DOMPurify.sanitize(String(html || ""), {
    WHOLE_DOCUMENT: true,
    RETURN_TRUSTED_TYPE: false,
    ADD_TAGS: ["style", "script", "iframe", "video", "audio", "source"],
    ADD_ATTR: ["allow", "allowfullscreen", "controls", "poster", "playsinline", "target", "rel", "data-elakay-pagebuilder-custom-js"],
    FORBID_TAGS: ["object", "embed", "base", "form"],
    FORBID_ATTR: ["srcset"],
  });

  if (typeof DOMParser === "undefined") return clean;
  const doc = new DOMParser().parseFromString(clean, "text/html");
  doc.querySelectorAll("script[src]").forEach((script) => script.remove());
  doc.querySelectorAll("meta[http-equiv]").forEach((node) => {
    if (String(node.getAttribute("http-equiv") || "").toLowerCase() === "refresh") node.remove();
  });
  doc.querySelectorAll("a[target='_blank']").forEach((link) => link.setAttribute("rel", "noopener noreferrer"));
  doc.querySelectorAll("iframe").forEach((frame) => {
    const src = safePageEmbedUrl(frame.getAttribute("src"));
    if (!src) frame.remove();
    else {
      frame.setAttribute("src", src);
      frame.setAttribute("sandbox", "allow-scripts allow-same-origin allow-presentation");
      frame.setAttribute("referrerpolicy", "no-referrer");
    }
  });
  const meta = doc.createElement("meta");
  meta.setAttribute("http-equiv", "Content-Security-Policy");
  meta.setAttribute("content", SRC_DOC_CSP);
  doc.head.prepend(meta);
  return `<!doctype html>\n${doc.documentElement.outerHTML}`;
}

export { SRC_DOC_CSP };
